/**
 * BasemapSwitcher - Mobile
 * 
 * Floating button to toggle between map styles.
 * Uses design tokens exclusively - no hardcoded values.
 */

import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon, border, shadow, touch } from '../../../shared/tokens/mobile';
import { BasemapStyle } from '../../../hooks/useMapData';

interface BasemapOption {
  id: BasemapStyle;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
}

const BASEMAP_OPTIONS: BasemapOption[] = [
  { id: 'outdoors', label: 'Terrain', icon: 'trail-sign-outline' },
  { id: 'streets', label: 'Streets', icon: 'car-outline' },
  { id: 'satellite', label: 'Satellite', icon: 'earth-outline' },
  { id: 'satellite-streets', label: 'Hybrid', icon: 'globe-outline' },
  { id: 'light', label: 'Light', icon: 'sunny-outline' },
  { id: 'dark', label: 'Dark', icon: 'moon-outline' },
];

interface BasemapSwitcherProps {
  currentBasemap: BasemapStyle;
  onBasemapChange: (basemap: BasemapStyle) => void;
}

export function BasemapSwitcher({ currentBasemap, onBasemapChange }: BasemapSwitcherProps) {
  const [showMenu, setShowMenu] = useState(false);

  const handleSelect = (basemap: BasemapStyle) => {
    onBasemapChange(basemap);
    setShowMenu(false);
  };

  return (
    <View style={styles.container}>
      <Pressable
        onPress={() => setShowMenu(!showMenu)}
        style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
        accessibilityLabel="Change map style"
        accessibilityRole="button"
      >
        <Ionicons name="layers-outline" size={icon.md} color={color.textPrimary} />
      </Pressable>

      {showMenu && (
        <>
          <Pressable style={styles.backdrop} onPress={() => setShowMenu(false)} />
          
          <View style={styles.menu}>
            {BASEMAP_OPTIONS.map((option) => {
              const isActive = currentBasemap === option.id;
              return (
                <Pressable
                  key={option.id}
                  onPress={() => handleSelect(option.id)}
                  style={[styles.menuItem, isActive && styles.menuItemActive]}
                >
                  <Ionicons
                    name={option.icon}
                    size={icon.sm}
                    color={isActive ? color.primary : color.textSecondary}
                  />
                  <Text style={[styles.menuItemText, isActive && styles.menuItemTextActive]}>
                    {option.label}
                  </Text>
                  {isActive && (
                    <Ionicons name="checkmark" size={icon.sm} color={color.primary} />
                  )}
                </Pressable>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: space.sm,
    left: space.sm,
    zIndex: 20,
  },
  button: {
    width: touch.minimum,
    height: touch.minimum,
    borderRadius: radius.md,
    backgroundColor: color.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: border.width.thin,
    borderColor: color.border,
    ...shadow.sm,
  },
  buttonPressed: {
    backgroundColor: color.pressed,
  },
  backdrop: {
    position: 'absolute',
    top: -space.xxl * 2,
    left: -space.xl,
    right: -space.xxl * 4,
    bottom: -space.xxl * 10,
    zIndex: -1,
  },
  menu: {
    position: 'absolute',
    top: touch.minimum + space.sm,
    left: 0,
    backgroundColor: color.surface,
    borderRadius: radius.md,
    borderWidth: border.width.thin,
    borderColor: color.border,
    minWidth: 140,
    overflow: 'hidden',
    ...shadow.sm,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: space.sm + space.xs / 2,
    paddingHorizontal: space.md,
    gap: space.sm,
  },
  menuItemActive: {
    backgroundColor: color.primaryLight,
  },
  menuItemText: {
    flex: 1,
    fontSize: text.body.fontSize,
    color: color.textPrimary,
  },
  menuItemTextActive: {
    color: color.primary,
    fontWeight: text.bodyMedium.fontWeight,
  },
});
